import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_MODULERUNTIME_API ?? '/api/v1';

export type ModuleRecord = {
  id: string;
  module_id: string;
  code: string;
  name: string;
  version: string;
  state: string;
  category: string;
  license_required: boolean;
  licensed: boolean;
  health_status: { status: string; checked_at: string };
  installed_at: string;
  updated_at: string;
};

export type ModuleManifest = {
  id: string;
  code: string;
  name: string;
  version: string;
  category: string;
  licenseRequired: boolean;
  dependencies: string[];
  permissions: string[];
};

export type RuntimeDashboard = {
  total_modules: number;
  installed_modules: number;
  enabled_modules: number;
  running_modules: number;
  error_modules: number;
  healthy_modules: number;
  runtime_healthy: boolean;
  recent_activity: Array<{ id: string; module_id: string; action: string; details: string; created_at: string }>;
};

export type DependencyGraph = {
  nodes: Array<{ module_id: string; name: string; version: string; state: string; dependencies: string[] }>;
  edges: Array<{ from: string; to: string; optional: boolean }>;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getScopeHeaders(),
      ...init?.headers,
    },
  });
  if (!res.ok) throw new Error(await res.text() || res.statusText);
  if (res.status === 204) return undefined as T;
  const json = await res.json();
  return (json.data ?? json) as T;
}

export const runtimeApi = {
  dashboard: () => request<RuntimeDashboard>('/runtime/dashboard'),

  listModules: () => request<{ installed: ModuleRecord[]; available: ModuleManifest[] }>('/runtime/modules'),

  getModule: (id: string) => request<ModuleRecord>(`/runtime/modules/${id}`),

  install: (moduleId: string) =>
    request<ModuleRecord>('/runtime/modules/install', { method: 'POST', body: JSON.stringify({ module_id: moduleId }) }),

  enable: (moduleId: string) =>
    request<ModuleRecord>('/runtime/modules/enable', { method: 'POST', body: JSON.stringify({ module_id: moduleId }) }),

  disable: (moduleId: string) =>
    request<ModuleRecord>('/runtime/modules/disable', { method: 'POST', body: JSON.stringify({ module_id: moduleId }) }),

  restart: (moduleId: string) =>
    request<ModuleRecord>('/runtime/modules/restart', { method: 'POST', body: JSON.stringify({ module_id: moduleId }) }),

  upgrade: (moduleId: string, version: string) =>
    request<ModuleRecord>('/runtime/modules/upgrade', { method: 'POST', body: JSON.stringify({ module_id: moduleId, version }) }),

  uninstall: (moduleId: string) =>
    request<void>('/runtime/modules/uninstall', { method: 'POST', body: JSON.stringify({ module_id: moduleId }) }),

  health: (id: string) => request<{ status: string }>(`/runtime/modules/${id}/health`),

  dependencies: (id: string) => request<DependencyGraph>(`/runtime/modules/${id}/dependencies`),

  events: (id: string) => request<unknown[]>(`/runtime/modules/${id}/events`),
};
